import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImageUploader from '../../components/admin/ImageUploader';
import FileUploader from '../../components/admin/FileUploader';
import RichTextEditor from '../../components/admin/RichTextEditor';
import GalleryUploader from '../../components/admin/GalleryUploader';
import { fetchAttachments, addAttachment, deleteAttachment } from '../../lib/attachmentsApi';
import {
  fetchNewsPostById,
  upsertNewsPost,
  deleteNewsPost,
  fetchNewsImages,
  addNewsImage,
  deleteNewsImage,
} from '../../lib/newsPostsApi';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from '../../lib/cmsUtils';

const emptyForm = {
  title: '',
  body: '',
  date: '',
  cover_image_url: '',
  status: 'draft',
};

export default function NewsPostForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState(emptyForm);
  const [gallery, setGallery] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const post = await fetchNewsPostById(id);
      setForm({
        title: post.title,
        body: post.body,
        date: toDatetimeLocalValue(post.date),
        cover_image_url: post.cover_image_url || '',
        status: post.status,
      });
      setGallery(await fetchNewsImages(id));
      setAttachments(await fetchAttachments({ newsPostId: id }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const ensureSaved = async () => {
    if (!isNew) return id;
    const saved = await upsertNewsPost({
      ...form,
      date: fromDatetimeLocalValue(form.date),
    });
    navigate(`/admin/news/${saved.id}`, { replace: true });
    return saved.id;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        date: fromDatetimeLocalValue(form.date),
      };
      if (!isNew) payload.id = id;
      await upsertNewsPost(payload);
      navigate('/admin/news');
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    await deleteNewsPost(id);
    navigate('/admin/news');
  };

  const handleGalleryAdd = async (url) => {
    const postId = await ensureSaved();
    const img = await addNewsImage(postId, url, gallery.length);
    setGallery((prev) => [...prev, img]);
  };

  const handleAttachmentUpload = async ({ url, name, type }) => {
    const postId = await ensureSaved();
    const att = await addAttachment({
      news_post_id: postId,
      file_url: url,
      file_name: name,
      file_type: type,
    });
    setAttachments((prev) => [...prev, att]);
  };

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-dark mb-8">{isNew ? 'New post' : 'Edit post'}</h1>

      <form onSubmit={handleSave} className="space-y-6 bg-white p-6 rounded-xl border border-gray-200">
        {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>}

        <div>
          <label className="block text-sm font-medium mb-1">Title *</label>
          <input required value={form.title} onChange={(e) => set('title', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Date *</label>
          <input required type="datetime-local" value={form.date} onChange={(e) => set('date', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2" />
        </div>

        <RichTextEditor value={form.body} onChange={(v) => set('body', v)} />

        <ImageUploader
          bucket="news-media"
          pathPrefix="covers"
          currentUrl={form.cover_image_url}
          onUpload={(url) => set('cover_image_url', url)}
          label="Cover image"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <select value={form.status} onChange={(e) => set('status', e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <GalleryUploader
          images={gallery}
          onAdd={handleGalleryAdd}
          onDelete={async (imgId) => {
            await deleteNewsImage(imgId);
            setGallery((prev) => prev.filter((i) => i.id !== imgId));
          }}
        />

        <FileUploader
          existingFiles={attachments}
          onUpload={handleAttachmentUpload}
          onDelete={async (attId) => {
            await deleteAttachment(attId);
            setAttachments((prev) => prev.filter((a) => a.id !== attId));
          }}
        />

        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={saving}
            className="bg-brand-purple text-white px-6 py-2 rounded-lg font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => navigate('/admin/news')}
            className="px-6 py-2 border border-gray-300 rounded-lg">Cancel</button>
          {!isNew && (
            <button type="button" onClick={handleDelete}
              className="ml-auto text-red-600 hover:underline">Delete post</button>
          )}
        </div>
      </form>
    </div>
  );
}
